import { ChatMessageType } from "../contracts";
import { isRecord } from "../utils/objectValueConversion";
import type { ChatMessageViewModel } from "./chatMessageViewModel";
import { isCommercialSimulatorSkuToolRequest, isCommercialSimulatorSubBrandToolRequest, isToolRequest, type CommercialSimulatorOptions, type CommercialSimulatorSubBrandOptions, type CommercialSimulatorSubBrandToolRequest, type CommercialSimulatorToolRequest, type ToolRequest } from "./toolRequestModels";

export type ToolResponse<
    TToolCall extends ToolRequest = ToolRequest,
    TUserSelection = unknown
> = {
    tool_call: TToolCall;
    tool_call_user_selection: TUserSelection;
};

export type CommercialSimulatorToolResponse = ToolResponse<
    CommercialSimulatorToolRequest,
    CommercialSimulatorOptions
>;

export type CommercialSimulatorSubBrandToolResponse = ToolResponse<
    CommercialSimulatorSubBrandToolRequest,
    CommercialSimulatorSubBrandOptions
>;

export const isToolResponse = (
    type: ChatMessageType,
    payload: unknown
): payload is ToolResponse => {
    if (!isRecord(payload) || type !== ChatMessageType.ToolResponse) {
        return false;
    }

    return (
        isToolRequest(ChatMessageType.ToolRequest, payload.tool_call)
        && "tool_call_user_selection" in payload
    );
};

export const getToolResponseMessageById = <TToolResponse extends ToolResponse = ToolResponse>(
    messages: ChatMessageViewModel[] | undefined,
    toolRequestId: string | undefined
): { message: ChatMessageViewModel; payload: TToolResponse } | undefined => {
    if (!messages || !toolRequestId) {
        return undefined;
    }

    for (const message of messages) {
        if (message.type !== ChatMessageType.ToolResponse) {
            continue;
        }

        const payload = message.payload;
        if (isToolResponse(message.type, payload) && payload.tool_call.id === toolRequestId) {
            return { message, payload: payload as TToolResponse };
        }
    }

    return undefined;
};

export const getToolResponseById = <TToolResponse extends ToolResponse = ToolResponse>(
    messages: ChatMessageViewModel[] | undefined,
    toolRequestId: string | undefined
): TToolResponse | undefined => {
    if (!messages || !toolRequestId) {
        return undefined;
    }

    for (const message of messages) {
        if (message.type !== ChatMessageType.ToolResponse) {
            continue;
        }

        const payload = message.payload;
        if (isToolResponse(message.type, payload) && payload.tool_call.id === toolRequestId) {
            return payload as TToolResponse;
        }
    }

    return undefined;
};

const isCommercialSimulatorOptions = (
    payload: unknown
): payload is CommercialSimulatorOptions => {
    if (!isRecord(payload)) {
        return false;
    }

    return (
        typeof payload.sku_id === "number"
        && typeof payload.sku_name === "string"
        && typeof payload.retailer_id === "number"
        && typeof payload.retailer_name === "string"
    );
};

const isCommercialSimulatorSubBrandOptions = (
    payload: unknown
): payload is CommercialSimulatorSubBrandOptions => {
    if (!isRecord(payload) || !Array.isArray(payload.products)) {
        return false;
    }

    return payload.products.every(product => (
        isRecord(product)
        && typeof product.id === "number"
        && typeof product.name === "string"
        && typeof product.retailer_id === "number"
        && typeof product.retailer_name === "string"
    ));
};

export const isCommercialSimulatorToolResponse = (
    payload: unknown
): payload is CommercialSimulatorToolResponse => {
    if (!isRecord(payload)) {
        return false;
    } 
    return (
        isCommercialSimulatorSkuToolRequest(payload.tool_call)
        && isCommercialSimulatorOptions(payload.tool_call_user_selection)
    );
};

export const isCommercialSimulatorSubBrandToolResponse = (
    payload: unknown
): payload is CommercialSimulatorSubBrandToolResponse => {
    if (!isRecord(payload)) {
        return false;
    }

    return (
        isCommercialSimulatorSubBrandToolRequest(payload.tool_call)
        && isCommercialSimulatorSubBrandOptions(
            payload.tool_call_user_selection
        )
    );
};
